import type { CompanyColumnDef } from '../interfaces/grid-column.types';

export interface GridSortState {
  readonly field: string;
  readonly direction: 'asc' | 'desc';
}

function stringifyCellValue(row: Record<string, unknown>, field: string): string {
  const value = row[field];
  return value === null || value === undefined ? '' : String(value).toLowerCase();
}

export function filterRows<T>(
  rows: readonly T[],
  columns: readonly CompanyColumnDef<T>[],
  globalTerm: string,
  columnFilters: Readonly<Record<string, string>>
): T[] {
  const normalizedGlobalTerm = globalTerm.trim().toLowerCase();
  const activeColumnFilters = Object.entries(columnFilters).filter(([, term]) => term.trim() !== '');

  if (!normalizedGlobalTerm && activeColumnFilters.length === 0) {
    return rows as T[];
  }

  return rows.filter(row => {
    const record = row as Record<string, unknown>;

    const matchesGlobalTerm =
      normalizedGlobalTerm === '' ||
      columns.some(column => stringifyCellValue(record, column.field).includes(normalizedGlobalTerm));

    const matchesColumnFilters = activeColumnFilters.every(([field, term]) =>
      stringifyCellValue(record, field).includes(term.trim().toLowerCase())
    );

    return matchesGlobalTerm && matchesColumnFilters;
  });
}

export function sortRows<T>(rows: readonly T[], sort: GridSortState | null): T[] {
  if (!sort) {
    return [...rows];
  }

  const direction = sort.direction === 'asc' ? 1 : -1;

  return [...rows].sort((a, b) => {
    const left = (a as Record<string, unknown>)[sort.field];
    const right = (b as Record<string, unknown>)[sort.field];

    if (left === right) return 0;
    if (left === null || left === undefined) return 1;
    if (right === null || right === undefined) return -1;

    return left > right ? direction : -direction;
  });
}

export function paginateRows<T>(rows: readonly T[], page: number, pageSize: number): T[] {
  const start = (page - 1) * pageSize;
  return rows.slice(start, start + pageSize);
}

export function defaultRowKey<T>(row: T, index: number): unknown {
  const id = (row as Record<string, unknown> | null)?.['id'];
  return id ?? index;
}
